import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
    ArrowLeft,
    ArrowRight,
    CircleNotch,
    Warning,
} from "@phosphor-icons/react";
import { fetchScanConfig, submitScan, trackEvent } from "../lib/api";

const STATUS = {
    LOADING: "loading",
    READY: "ready",
    SUBMITTING: "submitting",
    ERROR: "error",
};

export default function Scan() {
    const navigate = useNavigate();
    const location = useLocation();
    const prefill = location.state || {};
    const [status, setStatus] = useState(STATUS.LOADING);
    const [config, setConfig] = useState(null);
    const [error, setError] = useState("");

    const [index, setIndex] = useState(0);
    const [context, setContext] = useState(prefill.prefillContext || {});
    const [answers, setAnswers] = useState(prefill.prefillAnswers || {});

    useEffect(() => {
        let active = true;
        fetchScanConfig()
            .then((data) => {
                if (!active) return;
                setConfig(data);
                setStatus(STATUS.READY);
                trackEvent("scan_start");
            })
            .catch((e) => {
                if (!active) return;
                setError(
                    e?.response?.data?.detail ||
                        "Failed to load the diagnostic configuration.",
                );
                setStatus(STATUS.ERROR);
            });
        return () => { active = false; };
    }, []);

    // Build the linear step list: [context-step, ...questions]
    const steps = useMemo(() => {
        if (!config) return [];
        const list = [];
        list.push({ kind: "context" });
        for (const q of config.questions || []) {
            list.push({ kind: "question", q });
        }
        return list;
    }, [config]);

    const total = steps.length;
    const current = steps[index];

    const progressPct = total > 0 ? Math.round(((index + 1) / total) * 100) : 0;
    const isLast = index === total - 1;

    const canAdvance = useMemo(() => {
        if (!current) return false;
        if (current.kind === "context") {
            const fields = (config?.context_fields || []).map((f) => f.id);
            return fields.every((id) => Boolean(context[id]));
        }
        return Boolean(answers[current.q.id]);
    }, [current, context, answers, config]);

    const onSelectAnswer = (qid, value) => {
        setAnswers((p) => ({ ...p, [qid]: value }));
        // Auto-advance to next question (unless this is the final step).
        if (!isLast) {
            window.setTimeout(() => {
                setIndex((i) => (i === total - 1 ? i : i + 1));
                window.scrollTo({ top: 0, behavior: "instant" });
            }, 220);
        }
    };

    const onSelectContext = (id, value) =>
        setContext((p) => ({ ...p, [id]: value }));

    const onPrev = () => {
        if (index > 0) {
            setIndex(index - 1);
            window.scrollTo({ top: 0, behavior: "instant" });
        }
    };

    const onNext = async () => {
        if (!canAdvance) return;
        if (!isLast) {
            setIndex(index + 1);
            window.scrollTo({ top: 0, behavior: "instant" });
            return;
        }
        setStatus(STATUS.SUBMITTING);
        try {
            const result = await submitScan({ context, answers });
            trackEvent("scan_complete", { submissionId: result.id });
            navigate(`/result/${result.id}`);
        } catch (e) {
            setError(
                e?.response?.data?.detail ||
                    "Failed to submit your scan. Please try again.",
            );
            setStatus(STATUS.READY);
        }
    };

    if (status === STATUS.LOADING) {
        return (
            <main data-testid="scan-loading" className="flex min-h-[70vh] items-center justify-center">
                <div className="flex items-center gap-3 text-biks-text2">
                    <CircleNotch size={18} weight="bold" className="animate-spin text-biks-blue" />
                    Loading diagnostic...
                </div>
            </main>
        );
    }

    if (status === STATUS.ERROR) {
        return (
            <main data-testid="scan-error" className="flex min-h-[70vh] items-center justify-center px-6">
                <div className="max-w-md rounded-lg border border-biks-danger/50 bg-biks-danger/5 p-6">
                    <div className="flex items-center gap-2 text-biks-danger">
                        <Warning size={16} weight="bold" />
                        <span className="overline !text-biks-danger">Error</span>
                    </div>
                    <p className="mt-3 text-sm text-white">{error}</p>
                </div>
            </main>
        );
    }

    return (
        <main data-testid="scan-page" className="bg-biks-bg">
            {/* Progress strip */}
            <div className="sticky top-16 z-40 border-b border-biks-line/60 bg-biks-bg/95 backdrop-blur">
                <div className="mx-auto max-w-3xl px-6 py-4 lg:px-10">
                    <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.22em]">
                        <span className="text-biks-text3">
                            Step {index + 1} / {total}
                        </span>
                        <span className="font-mono-data text-biks-blueSoft">{progressPct}%</span>
                    </div>
                    <div className="mt-3 h-[3px] w-full rounded-full bg-biks-surface2/80">
                        <div
                            data-testid="scan-progress-bar"
                            className="h-[3px] rounded-full bg-gradient-to-r from-biks-blue to-biks-blueSoft transition-all duration-300"
                            style={{ width: `${progressPct}%` }}
                        />
                    </div>
                </div>
            </div>

            <div className="mx-auto max-w-3xl px-6 pb-32 pt-12 lg:px-10 lg:pt-16">
                {current?.kind === "context" ? (
                    <ContextStep
                        fields={config.context_fields || []}
                        value={context}
                        onSelect={onSelectContext}
                    />
                ) : (
                    <QuestionStep
                        q={current.q}
                        domains={config.domains || []}
                        selected={answers[current.q.id]}
                        onSelect={(v) => onSelectAnswer(current.q.id, v)}
                    />
                )}

                {error && (
                    <div
                        data-testid="scan-inline-error"
                        className="mt-6 flex items-start gap-2 rounded-md border border-biks-danger/60 bg-biks-danger/5 p-4 text-sm text-biks-danger"
                    >
                        <Warning size={14} weight="bold" className="mt-1" />
                        <span>{error}</span>
                    </div>
                )}

                <div className="mt-10 flex items-center justify-between border-t border-biks-line/50 pt-6">
                    <button
                        type="button"
                        data-testid="scan-prev-button"
                        onClick={onPrev}
                        disabled={index === 0}
                        className="inline-flex items-center gap-2 rounded-md border border-biks-line px-5 py-3 text-sm text-white transition-colors hover:border-biks-blueSoft disabled:cursor-not-allowed disabled:opacity-40"
                    >
                        <ArrowLeft size={14} weight="bold" />
                        Previous
                    </button>
                    <button
                        type="button"
                        data-testid="scan-next-button"
                        onClick={onNext}
                        disabled={!canAdvance || status === STATUS.SUBMITTING}
                        className="inline-flex items-center gap-2 rounded-md bg-biks-blue px-6 py-3 text-sm font-semibold uppercase tracking-[0.14em] text-white transition-colors hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                        {status === STATUS.SUBMITTING ? (
                            <>
                                <CircleNotch size={14} weight="bold" className="animate-spin" />
                                Scoring...
                            </>
                        ) : (
                            <>
                                {isLast ? "Reveal diagnostic" : "Next"}
                                <ArrowRight size={14} weight="bold" />
                            </>
                        )}
                    </button>
                </div>
            </div>
        </main>
    );
}

function ContextStep({ fields, value, onSelect }) {
    return (
        <div data-testid="scan-context-step" className="animate-fade-up">
            <div className="section-title">Context</div>
            <h1 className="font-display mt-5 text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl">
                A few facts before we begin.
            </h1>
            <p className="mt-4 max-w-xl text-base text-biks-text2">
                Your answers about type, industry and stage help us read your result more precisely. They can only make the assessment stricter where it matters. They never inflate your score.
            </p>
            <p data-testid="scan-idea-blind-note" className="mt-3 max-w-xl text-sm text-biks-text3">
                You will not be asked to describe your idea. You only agree or disagree with 27 structural statements.
            </p>

            <div className="mt-10 flex flex-col gap-8">
                {fields.map((f) => (
                    <div key={f.id} data-testid={`scan-context-${f.id}`}>
                        <div className="section-title mb-3">{f.label}</div>
                        {f.helper && (
                            <div className="mb-4 text-sm text-biks-text2">{f.helper}</div>
                        )}
                        <div className="flex flex-wrap gap-2">
                            {f.options.map((o) => {
                                const active = value[f.id] === o.value;
                                return (
                                    <button
                                        key={o.value}
                                        type="button"
                                        data-testid={`scan-context-${f.id}-${o.value}`}
                                        onClick={() => onSelect(f.id, o.value)}
                                        className={`rounded-md border px-4 py-2.5 text-sm transition-colors ${
                                            active
                                                ? "border-biks-blue bg-biks-blue/15 text-white"
                                                : "border-biks-line bg-biks-surface/40 text-biks-text2 hover:border-biks-blueSoft hover:text-white"
                                        }`}
                                    >
                                        {o.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

const GROUP_LABELS = {
    domain: "Structural question",
    flag: "Critical signal",
    innovation: "Innovation context",
    meta: "Self-assessment",
};

// Renders helper text, turning **phrase** into a highlighted, readable emphasis.
function HelperText({ text }) {
    const parts = text.split("**");
    return parts.map((p, i) =>
        i % 2 === 1 ? (
            <strong key={i} className="font-semibold text-white">{p}</strong>
        ) : (
            <span key={i}>{p}</span>
        ),
    );
}

function QuestionStep({ q, domains, selected, onSelect }) {
    const domainLabel = q.domain_id
        ? (domains.find((d) => d.id === q.domain_id)?.display_label
            || domains.find((d) => d.id === q.domain_id)?.label
            || "")
        : "";
    const groupLabel = q.group === "domain" && domainLabel
        ? domainLabel
        : GROUP_LABELS[q.group] || "";

    return (
        <div key={q.id} data-testid="scan-question" className="animate-fade-up">
            <div className="section-title" data-testid="scan-category">{groupLabel}</div>
            <h1
                data-testid="scan-question-prompt"
                className="font-display mt-5 text-2xl font-bold leading-snug tracking-tight text-white sm:text-3xl"
            >
                {q.prompt}
            </h1>
            {q.helper && (
                <p
                    data-testid="scan-question-helper"
                    className="mt-3 max-w-2xl text-sm leading-relaxed text-biks-text2"
                >
                    <HelperText text={q.helper} />
                </p>
            )}

            <div role="radiogroup" aria-label={q.prompt} className="mt-10 flex flex-col gap-3">
                {q.options.map((opt) => {
                    const isActive = selected === opt.value;
                    const isNei = opt.value === "NEI";
                    return (
                        <button
                            key={opt.value}
                            type="button"
                            role="radio"
                            aria-checked={isActive}
                            data-testid={`scan-option-${q.id}-${opt.value}`}
                            onClick={() => onSelect(opt.value)}
                            className={`group flex items-start gap-4 rounded-lg border p-4 text-left transition-colors duration-150 ${
                                isActive
                                    ? "border-biks-blue bg-biks-blue/15"
                                    : isNei
                                        ? "border-biks-line bg-biks-surface/30 text-biks-text2 hover:border-biks-blueSoft"
                                        : "border-biks-line bg-biks-surface/50 hover:border-biks-blueSoft"
                            }`}
                        >
                            <span
                                className={`mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${
                                    isActive
                                        ? "border-biks-blue"
                                        : "border-biks-line group-hover:border-biks-blueSoft"
                                }`}
                            >
                                <span className={`h-2 w-2 rounded-full ${isActive ? "bg-biks-blue" : "bg-transparent"}`} />
                            </span>
                            <span className="text-base text-white">{opt.label}</span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
